import { aiService } from './aiService';
import { storage } from "./storage";

/**
 * Service pour parser une annonce Leboncoin via Gemini AI
 * L'extension envoie le HTML et Gemini extrait les informations structurées
 */

export interface LeboncoinParsedData {
    url: string;
    adId?: string;
    title?: string;
    price?: number;
    monthlyRentWithCharges?: number; // Loyer charges comprises
    location?: string;
    address?: string; // Adresse complète ou quartier
    zipCode?: string; // Code postal (toujours disponible sur LBC)
    city?: string; // Ville (toujours disponible sur LBC)
    description?: string;
    surface?: number;
    rooms?: number;
    bedrooms?: number;
    bathrooms?: number;
    maxGuests?: number;
    energyClass?: string;
    images?: string[];
    ownerPseudo?: string; // Pseudo LBC (ex: Magi)
    ownerFirstName?: string; // Prénom extrait ou "A définir"
    ownerLastName?: string; // Nom de famille extrait ou pseudo LBC
    note?: string; // Message d'avertissement si scraping non disponible
}

// Plus d'extraction manuelle - tout passe par Gemini AI

/**
 * Type pour l'API Chrome Extension (si disponible)
 */
type ChromeRuntime = {
    sendMessage: (extensionId: string, message: any, responseCallback?: (response: any) => void) => void;
    lastError?: { message?: string };
    id?: string;
};

/**
 * Récupère l'API Chrome si disponible
 */
function getChromeRuntime(): ChromeRuntime | null {
    if (typeof window !== 'undefined' && (window as any).chrome && (window as any).chrome.runtime) {
        return (window as any).chrome.runtime as ChromeRuntime;
    }
    return null;
}

/**
 * Récupère l'ID de l'extension Placid Manager
 * L'ID peut être stocké dans localStorage ou détecté automatiquement
 */
function getExtensionId(): string | null {
    // Essayer de récupérer depuis localStorage (configuré par l'utilisateur)
    const storedId = localStorage.getItem('placid_extension_id');
    if (storedId && storedId.trim()) {
        return storedId.trim();
    }

    // Essayer de détecter automatiquement en envoyant un ping à l'extension
    // On essaie de détecter via chrome.runtime.id si disponible
    const chromeRuntime = getChromeRuntime();
    if (chromeRuntime && chromeRuntime.id) {
        // Si on est dans le contexte de l'extension, utiliser son ID
        // Mais ce n'est généralement pas le cas dans l'application web
        return chromeRuntime.id;
    }

    // Note: La détection automatique n'est pas fiable depuis une page web
    // L'utilisateur doit configurer l'ID manuellement dans les paramètres
    return null;
}

/**
 * Vérifie si l'extension Chrome est disponible
 */
async function checkExtensionAvailable(): Promise<boolean> {
    return new Promise((resolve) => {
        const chromeRuntime = getChromeRuntime();
        if (!chromeRuntime || !chromeRuntime.sendMessage) {
            resolve(false);
            return;
        }

        const extensionId = getExtensionId();
        if (!extensionId) {
            // Pas d'ID configuré, essayer avec postMessage (méthode alternative)
            resolve(false);
            return;
        }

        // Essayer d'envoyer un message de ping à l'extension
        // Si l'extension répond, elle est disponible
        try {
            chromeRuntime.sendMessage(
                extensionId,
                { action: 'ping' },
                (response) => {
                    if (chromeRuntime.lastError) {
                        // Extension non disponible ou erreur
                        console.log('Extension ping failed:', chromeRuntime.lastError.message);
                        resolve(false);
                    } else {
                        // Extension disponible
                        resolve(true);
                    }
                }
            );
            
            // Timeout après 500ms
            setTimeout(() => resolve(false), 500);
        } catch (e) {
            // Erreur = extension non disponible
            resolve(false);
        }
    });
}

/**
 * Essaie de communiquer avec l'extension Chrome pour obtenir le HTML
 * Plus d'extraction manuelle, on récupère juste le HTML pour Gemini
 */
async function tryExtensionParse(url: string): Promise<LeboncoinParsedData | null> {
    // Cette fonction n'est plus utilisée car on passe directement par tryExtensionParseWithHTML
    // Gardée pour compatibilité mais ne sera jamais appelée
    return null;
}

/**
 * Parse les données Leboncoin extraites par Gemini
 */
function parseGeminiResponse(geminiResponse: string, url: string): LeboncoinParsedData {
    try {
        // Nettoyer la réponse de Gemini (peut contenir des markdown ou du texte avant/après)
        let cleanedResponse = geminiResponse.trim();
        
        // Enlever les markdown code blocks si présents
        cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '');
        
        // Extraire le JSON si entouré de texte
        const jsonMatch = cleanedResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            cleanedResponse = jsonMatch[0];
        }
        
        const parsed = JSON.parse(cleanedResponse);
        
        // Construire le nom du lead : prénom + nom si disponibles, sinon nom_lead (pseudo)
        // Si c'est un pseudo (nom_lead existe mais pas de prénom/nom séparés), utiliser nom_lead
        // Si c'est un nom complet (prénom + nom), les combiner
        let ownerPseudo = parsed.nom_lead || '';
        let ownerFirstName = '';
        let ownerLastName = '';
        
        if (parsed.prenom_lead && parsed.nom_famille_lead) {
            // Nom complet détecté : utiliser prénom + nom
            ownerPseudo = `${parsed.prenom_lead} ${parsed.nom_famille_lead}`;
            ownerFirstName = parsed.prenom_lead;
            ownerLastName = parsed.nom_famille_lead;
        } else if (parsed.prenom_lead && !parsed.nom_famille_lead) {
            // Seulement prénom
            ownerPseudo = parsed.prenom_lead;
            ownerFirstName = parsed.prenom_lead;
            ownerLastName = '';
        } else if (!parsed.prenom_lead && !parsed.nom_famille_lead && parsed.nom_lead) {
            // C'est un pseudo : utiliser tel quel
            ownerPseudo = parsed.nom_lead;
            ownerFirstName = 'A définir';
            ownerLastName = parsed.nom_lead;
        } else {
            // Aucune info : utiliser le pseudo par défaut
            ownerFirstName = 'A définir';
            ownerLastName = parsed.nom_lead || '';
        }
        
        // Extraire code postal et ville (toujours disponibles sur LBC)
        const zipCode = parsed.code_postal || '';
        const city = parsed.ville || '';
        
        // L'adresse peut contenir l'adresse complète si trouvée, ou le quartier
        const address = parsed.adresse_quartier || '';
        
        // Construire la location pour l'affichage
        const location = zipCode && city ? `${zipCode} ${city}` : (city || zipCode || '');

        const data: LeboncoinParsedData = {
            url: url,
            adId: null,
            title: parsed.type_appartement || '',
            price: parsed.loyer_cc || null,
            monthlyRentWithCharges: parsed.loyer_cc || null,
            location: location,
            address: address, // Adresse complète ou quartier
            zipCode: zipCode, // Code postal (toujours disponible)
            city: city, // Ville (toujours disponible)
            description: parsed.description || '',
            surface: parsed.surface_m2 ? Math.round(parsed.surface_m2) : null,
            rooms: parsed.pieces || null,
            bedrooms: parsed.details_deduits?.chambres || null,
            bathrooms: parsed.details_deduits?.sdb || null,
            maxGuests: parsed.details_deduits?.invites_estimes || null,
            energyClass: '',
            images: [],
            ownerPseudo: ownerPseudo,
            ownerFirstName: ownerFirstName,
            ownerLastName: ownerLastName,
        };

        // Extract ad ID from URL
        const adIdMatch = url.match(/\/(\d+)\.htm/);
        if (adIdMatch) {
            data.adId = adIdMatch[1];
        }

        return data;
    } catch (e) {
        console.error('Error parsing Gemini response:', e);
        console.error('Raw response:', geminiResponse);
        // Retourner des données minimales en cas d'erreur
        const adIdMatch = url.match(/\/(\d+)\.htm/);
        return {
            url: url,
            adId: adIdMatch ? adIdMatch[1] : undefined,
            title: '',
            price: null,
            monthlyRentWithCharges: null,
            location: '',
            address: '',
            zipCode: '',
            city: '',
            description: '',
            surface: null,
            rooms: null,
            bedrooms: null,
            bathrooms: null,
            maxGuests: null,
            energyClass: '',
            images: [],
            ownerPseudo: '',
            ownerFirstName: 'A définir',
            ownerLastName: '',
            note: 'Erreur lors de l\'extraction des données par Gemini'
        };
    }
}

export const leboncoinService = {
    /**
     * Parse une URL Leboncoin en utilisant Gemini AI pour extraire les données depuis le HTML
     * L'extension Chrome envoie le HTML, et Gemini extrait les informations structurées
     */
    parseAd: async (url: string, providedHTML?: string): Promise<LeboncoinParsedData> => {
        try {
            if (!url || !url.includes('leboncoin.fr')) {
                throw new Error('URL Leboncoin invalide');
            }

            // Si on a le HTML, utiliser Gemini directement
            if (providedHTML) {
                return await parseAdWithGemini(url, providedHTML);
            }

            // Sinon, essayer via l'extension Chrome qui enverra le HTML
            const extensionId = getExtensionId();
            if (!extensionId) {
                throw new Error('EXTENSION_REQUIRED');
            }

            const extensionAvailable = await checkExtensionAvailable();
            if (!extensionAvailable) {
                throw new Error('EXTENSION_NOT_ACCESSIBLE');
            }

            // Demander à l'extension d'extraire le HTML et de l'envoyer pour parsing avec Gemini
            const htmlData = await tryExtensionGetHTML(url);
            if (!htmlData) {
                throw new Error('EXTENSION_EXTRACTION_FAILED');
            }

            // Parser le HTML avec Gemini
            console.log('✅ HTML récupéré via l\'extension Chrome, parsing avec Gemini...');
            return await parseAdWithGemini(url, htmlData);

        } catch (e: any) {
            console.error('Leboncoin Parse Error:', e);
            
            // Gérer les erreurs spécifiques
            if (e.message === 'EXTENSION_REQUIRED') {
                throw new Error('EXTENSION_REQUIRED');
            } else if (e.message === 'EXTENSION_NOT_ACCESSIBLE') {
                throw new Error('EXTENSION_NOT_ACCESSIBLE');
            } else if (e.message === 'EXTENSION_EXTRACTION_FAILED') {
                throw new Error('EXTENSION_EXTRACTION_FAILED');
            }
            
            throw e;
        }
    },

    /**
     * Parse une annonce Leboncoin avec Gemini en utilisant le HTML fourni
     */
    parseAdWithGemini: async (url: string, html: string): Promise<LeboncoinParsedData> => {
        try {
            const response = await aiService.parseLeboncoin(html);
            // aiService.parseLeboncoin retourne déjà un objet JSON parsé
            return parseGeminiResponse(JSON.stringify(response), url);
        } catch (e: any) {
            console.error('Gemini parsing error:', e);
            
            // Détecter l'erreur de quota (429)
            const isQuotaError = 
                e.message?.includes('quota') || 
                e.message?.includes('429') ||
                e.message?.includes('RESOURCE_EXHAUSTED') ||
                e.message?.includes('QUOTA_EXCEEDED');
            
            if (isQuotaError) {
                throw new Error(`QUOTA_EXCEEDED: Le quota de l'API Gemini a été dépassé. Veuillez réessayer dans quelques minutes. Pour plus d'informations, consultez https://ai.google.dev/gemini-api/docs/rate-limits`);
            }
            
            throw new Error('Erreur lors de l\'extraction par Gemini: ' + (e.message || 'Erreur inconnue'));
        }
    }
};

/**
 * Parse une annonce avec Gemini en utilisant le HTML fourni
 */
async function parseAdWithGemini(url: string, html: string): Promise<LeboncoinParsedData> {
    return leboncoinService.parseAdWithGemini(url, html);
}

/**
 * Essaie de récupérer le HTML via l'extension (sans parsing, juste le HTML)
 */
async function tryExtensionGetHTML(url: string): Promise<string | null> {
    return new Promise((resolve) => {
        const chromeRuntime = getChromeRuntime();
        if (!chromeRuntime || !chromeRuntime.sendMessage) {
            resolve(null);
            return;
        }

        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve(null);
            return;
        }

        try {
            chromeRuntime.sendMessage(
                extensionId,
                { action: 'parse-leboncoin-html', url: url },
                (response) => {
                    if (chromeRuntime.lastError) {
                        console.log('Extension error:', chromeRuntime.lastError.message);
                        resolve(null);
                    } else if (response && response.success && response.html) {
                        resolve(response.html);
                    } else {
                        resolve(null);
                    }
                }
            );
            
            setTimeout(() => {
                resolve(null);
            }, 15000); // Timeout plus long car l'extension doit ouvrir un onglet
        } catch (e) {
            console.error('Error communicating with extension:', e);
            resolve(null);
        }
    });
}




